import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';

// SVG Icons
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const LogoIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    <circle cx="12" cy="12" r="4" />
  </svg>
);

// New SVG Icons for features
const ChartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2M7 10l3-3 3 3 4-4" />
    <path d="M17 10V4h-3" />
  </svg>
);

const TargetIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const LightningIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <circle cx="12" cy="16" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('user');
    if (token && user) {
      navigate('/');
    }
  }, [navigate]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      
      if (!clientId) {
        setError('Google Client ID not configured. Please check your environment variables.');
        setLoading(false);
        return;
      }

      // Create OAuth2 client
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${clientId}&` +
        `redirect_uri=${window.location.origin}/auth/callback&` +
        `response_type=token&` +
        `scope=email profile&` +
        `prompt=select_account`;

      // Redirect to Google login
      window.location.href = authUrl;
      
    } catch (err: any) {
      console.error('Google login failed:', err);
      setError(err.message || 'Failed to login with Google');
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Left Panel - Branding Only */}
      <div className="login-left-panel">
        <div className="left-panel-content">
          <div className="left-panel-header">
            <div className="left-panel-logo">
              <LogoIcon />
              <span className="left-panel-brand">ZiTech AI</span>
            </div>
          </div>
          
          <div className="left-panel-main">
            <h1 className="left-panel-title">
              Football Prediction<br />Intelligence
            </h1>
            <p className="left-panel-description">
              Advanced AI-powered predictions for football matches. Get real-time analysis, 
              team statistics, and accurate forecasts for leagues worldwide.
            </p>
            
            <div className="feature-grid">
              <div className="feature-grid-item">
                <div className="feature-grid-icon">
                  <ChartIcon />
                </div>
                <div className="feature-grid-text">
                  <h3>Real-time Analysis</h3>
                  <p>Live match data and instant predictions</p>
                </div>
              </div>
              
              <div className="feature-grid-item">
                <div className="feature-grid-icon">
                  <TargetIcon />
                </div>
                <div className="feature-grid-text">
                  <h3>High Accuracy</h3>
                  <p>ML models trained on historical data</p>
                </div>
              </div>
              
              <div className="feature-grid-item">
                <div className="feature-grid-icon">
                  <GlobeIcon />
                </div>
                <div className="feature-grid-text">
                  <h3>Global Coverage</h3>
                  <p>50+ leagues from around the world</p>
                </div>
              </div>
              
              <div className="feature-grid-item">
                <div className="feature-grid-icon">
                  <LightningIcon />
                </div>
                <div className="feature-grid-text">
                  <h3>Fast Updates</h3>
                  <p>Real-time odds and statistics</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="left-panel-footer">
            <p>© 2024 ZiTech AI. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="login-right-panel">
        <div className="login-form-container">
          {/* Logo and Brand (Mobile Only) */}
          <div className="mobile-brand">
            <div className="mobile-brand-icon">
              <LogoIcon />
            </div>
            <h1 className="mobile-brand-name">ZiTech AI</h1>
            <p className="mobile-brand-tagline">Football Prediction Intelligence</p>
          </div>

          {/* Login Form */}
          <div className="login-form-content">
            <h2 className="form-title">Welcome Back</h2>
            <p className="form-subtitle">Sign in to access your account</p>

            {/* Error Message */}
            {error && (
              <div className="login-error">
                <span className="error-icon"><ErrorIcon /></span>
                <span>{error}</span>
              </div>
            )}

            {/* Google Login Button */}
            <button
              className="google-login-btn"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner-small"></div>
                  <span>Connecting to Google...</span>
                </>
              ) : (
                <>
                  <GoogleIcon />
                  <span>Sign in with Google</span>
                </>
              )}
            </button>

            {/* Terms */}
            <p className="terms-text">
              By continuing, you agree to our{' '}
              <a href="/terms" className="terms-link">Terms of Service</a>{' '}
              and{' '}
              <a href="/privacy" className="terms-link">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;